import { openUpgradePrompt } from '@/components/upgrade/upgrade-prompt.store';
import { useFeatureAccess } from '@/hooks/use-feature-access.hook';
import { useMemo, type MouseEvent } from 'react';
import { lockedUpgradeReason } from './locked-upgrade.util';

/** A click on a locked sidebar entry, with the entry's destination id. */
export type LockedUpgradeClick = (
  id: string,
  event: MouseEvent<HTMLElement>
) => void;

/**
 * What a click on a locked sidebar entry does, read once per sidebar and
 * handed down to its entries. A plain click opens the upgrade prompt for what
 * the entry unlocks instead of its page; a modified click (new tab, new
 * window) still follows the link.
 *
 * Undefined, so every locked entry follows its link, wherever the locked page
 * shows a gate of its own that suits the workspace better than the prompt:
 * AppSumo-only workspaces (their upgrades are AppSumo codes) and workspaces
 * offered a card trial they haven't started.
 */
export function useLockedUpgradeClick(): LockedUpgradeClick | undefined {
  const { isAppSumoOnly, trial } = useFeatureAccess();
  const trialOffered = Boolean(trial?.card_required && !trial.started_at);
  return useMemo(() => {
    if (isAppSumoOnly || trialOffered) return undefined;
    return (id, event) => {
      const reason = lockedUpgradeReason(id);
      if (!reason || event.defaultPrevented) return;
      if (
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      )
        return;
      event.preventDefault();
      openUpgradePrompt({ reason, source: 'sidebar_locked' });
    };
  }, [isAppSumoOnly, trialOffered]);
}
